import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser } from '@wix/da-papi-types';
import MailIcon from '@wix/da-ds/pkg/Icons/24x24/Mail';
import {
  withVerification,
  WithVerificationProps,
} from '@wix/da-shared-react/pkg/VerificationPopup/withVerification';
import { NullableDropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import BlockIcon from '@wix/da-ds/pkg/Icons/24x24/Block';
import EllipsisIcon from '@wix/da-ds/pkg/Icons/Ellipsis';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { ProfileOwnerWatchButton } from '@wix/da-shared-react/pkg/Button';
import IconButtonWithContextMenu from '@wix/da-shared-react/pkg/Dropdown/Preset/IconButtonWithContextMenu';
import { RestrictLoggedOutPayload } from '@wix/da-shared-react/pkg/redux/loggedout/actionCreators';
import { BiEventType, ReferralType } from '@wix/da-bi/pkg/constants';
import BaseSubNavBarDesktop, {
  Props as BaseProps,
} from '../../BaseSubNavBarDesktopInner';
import WatchButtonConnectContainer from '../../../WatchButton.container';
import RightSideButtonWrapper from '../../BaseSubNavBarDesktopInner/legos/RightSideButtonWrapper';
import LargeButtonsWrapper from '../../BaseSubNavBarDesktopInner/legos/LargeButtonsWrapper';
import ProfileSkinsButton from '../_partials/ProfileSkinsButton';
import ShareButton from './_partials/ShareButton';
import GiveButton from './_partials/GiveButton';
import { CollapsingElementsContainer } from '../../CollapsingElements';
import { CollapsingElementId } from '../../CollapsingElements/types';
import s from './UserProfileSubNavDesktopInner.scss';

const WatchButton = WatchButtonConnectContainer(ProfileOwnerWatchButton);

export interface Props extends BaseProps {
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  isLoggedIn: boolean;
  isProfileOwnerWatchingCurrentUser: boolean;
  blockProfileOwner: () => void;
  restrictLoggedOut: (payload: RestrictLoggedOutPayload) => void;
}

enum COLLAPSING_BUTTON {
  MESSAGE = 'message',
  SHARE = 'share',
  GIVE = 'give',
}

const UserProfileSubNavDesktopInner: React.FC<
  Props & WithVerificationProps
> = ({
  isSticky,
  profileOwnerUser,
  isOwner,
  isLoggedIn,
  isProfileOwnerWatchingCurrentUser,
  isVerificationNeeded,
  pushVerificationPopup,
  blockProfileOwner,
  restrictLoggedOut,
}) => {
  const [collapsedIds, setCollapsedIds] = useState<CollapsingElementId[]>([]);
  const messageButtonRef = useRef<HTMLDivElement | null>(null);
  const shareButtonRef = useRef<HTMLDivElement | null>(null);
  const giveButtonRef = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();

  const collapsingButtons = [
    {
      id: COLLAPSING_BUTTON.MESSAGE,
      ref: messageButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.SHARE,
      ref: shareButtonRef,
    },
    {
      id: COLLAPSING_BUTTON.GIVE,
      ref: giveButtonRef,
    },
  ];

  const collapsedButtonsAsMenuItems = {
    [COLLAPSING_BUTTON.MESSAGE]: {
      icon: <MailIcon />,
      label: t(`navigation.subnavigation.message`),
      onClick: isVerificationNeeded
        ? () => pushVerificationPopup('notes')
        : undefined,
      link: isVerificationNeeded
        ? undefined
        : Url.userNoteLink(profileOwnerUser.username),
    },
  };

  const renderSendMessageButton = () => {
    let onClick: (() => void) | undefined = undefined;
    if (!isLoggedIn) {
      onClick = () =>
        restrictLoggedOut({ restrictedActionName: ReferralType.NOTE });
    } else if (isVerificationNeeded) {
      onClick = () => pushVerificationPopup('notes');
    }
    const href = Url.userNoteLink(profileOwnerUser.username);
    return (
      <SolidButton
        size="large"
        variant="main"
        biData={{
          evid: BiEventType.SEND_NOTE_CLICK,
          recipientid: profileOwnerUser.useridUuid,
        }}
        {...(onClick && { onClick })}
        {...(!onClick && { href })}
      >
        {t(`navigation.subnavigation.message`)}
      </SolidButton>
    );
  };

  const renderWatchButton = () => {
    return (
      <WatchButton
        className={s['watch-button']}
        allowUnsubscribe={false}
        isWatchingBack={isProfileOwnerWatchingCurrentUser}
      />
    );
  };

  const getMoreActionsMenuItems = () => {
    const menuItems: Array<NullableDropdownItem> = [];

    if (collapsedIds.length) {
      const collapsedIntoMenuItems = collapsedIds.map(
        id => collapsedButtonsAsMenuItems[id]
      );
      menuItems.push(...collapsedIntoMenuItems);
    }

    if (isLoggedIn && !isOwner) {
      menuItems.push({
        icon: <BlockIcon />,
        label: t('navigation.subnavigation.blockUser'),
        onClick: blockProfileOwner,
      });
    }

    return menuItems;
  };

  const renderMoreActionsIconDropdown = () => {
    const menuItems = getMoreActionsMenuItems();
    if (!menuItems.length) {
      return null;
    }

    return (
      <RightSideButtonWrapper>
        <IconButtonWithContextMenu items={menuItems} withButton>
          <EllipsisIcon withPadding />
        </IconButtonWithContextMenu>
      </RightSideButtonWrapper>
    );
  };

  const rightSideContent = isOwner ? (
    <RightSideButtonWrapper>
      <ProfileSkinsButton />
    </RightSideButtonWrapper>
  ) : (
    <>
      {renderMoreActionsIconDropdown()}
      <RightSideButtonWrapper
        innerRef={shareButtonRef}
        isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.SHARE)}
      >
        <ShareButton profileOwner={profileOwnerUser} />
      </RightSideButtonWrapper>
      <RightSideButtonWrapper
        innerRef={giveButtonRef}
        isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.GIVE)}
      >
        <GiveButton />
      </RightSideButtonWrapper>
      <LargeButtonsWrapper>
        <RightSideButtonWrapper
          innerRef={messageButtonRef}
          isCollapsed={collapsedIds.includes(COLLAPSING_BUTTON.MESSAGE)}
        >
          {renderSendMessageButton()}
        </RightSideButtonWrapper>
        <RightSideButtonWrapper>{renderWatchButton()}</RightSideButtonWrapper>
      </LargeButtonsWrapper>
    </>
  );
  return (
    <BaseSubNavBarDesktop
      isSticky={isSticky}
      rightSideContent={
        <CollapsingElementsContainer
          collapsingElements={collapsingButtons}
          onCollapsedIdsChange={setCollapsedIds}
          justifyContent="flex-end"
        >
          {rightSideContent}
        </CollapsingElementsContainer>
      }
    />
  );
};
UserProfileSubNavDesktopInner.displayName = 'UserProfileSubNavDesktopInner';

export default withVerification(UserProfileSubNavDesktopInner);
